import { useCallback } from 'react';

export interface FlyerData {
  titulo: string;
  codigo: string;
  tipo: string;
  bairro: string;
  cidade: string;
  preco: number;
  quartos?: number;
  banheiros?: number;
  vagas?: number;
  area_total?: number;
  area_construida?: number;
  foto_capa_url?: string;
  fotos?: { url: string }[];
  slug: string;
}

// Carrega imagem como ImageBitmap (suporta CORS via proxy se necessário)
async function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => {
      // Tenta sem crossOrigin como fallback
      const img2 = new Image();
      img2.onload = () => resolve(img2);
      img2.onerror = reject;
      img2.src = src;
    };
    img.src = src;
  });
}

function formatCurrencyCanvas(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(value);
}

// Quebra texto longo em múltiplas linhas
function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    const test = current ? `${current} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && current) {
      lines.push(current);
      current = word;
    } else {
      current = test;
    }
  }
  if (current) lines.push(current);
  return lines;
}

// Desenha retângulo com bordas arredondadas
function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number
) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

export function useFlyerGenerator() {
  const generateFlyer = useCallback(async (data: FlyerData): Promise<string> => {
    // Canvas 1080x1350 (formato Instagram/WhatsApp 4:5)
    const W = 1080;
    const H = 1350;

    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d')!;

    // ── Fundo branco ──────────────────────────────────────────────────────────
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, W, H);

    // ── Foto principal (topo) ─────────────────────────────────────────────────
    const fotoUrl = data.foto_capa_url || data.fotos?.[0]?.url;
    const FOTO_H = 680;

    if (fotoUrl) {
      try {
        const img = await loadImage(fotoUrl);
        // Cover crop centralizado
        const scale = Math.max(W / img.width, FOTO_H / img.height);
        const sw = W / scale;
        const sh = FOTO_H / scale;
        const sx = (img.width - sw) / 2;
        const sy = (img.height - sh) / 2;
        ctx.drawImage(img, sx, sy, sw, sh, 0, 0, W, FOTO_H);
      } catch {
        // Fallback: gradiente laranja
        const grad = ctx.createLinearGradient(0, 0, W, FOTO_H);
        grad.addColorStop(0, '#ea580c');
        grad.addColorStop(1, '#c2410c');
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, W, FOTO_H);
        ctx.fillStyle = 'rgba(255,255,255,0.15)';
        ctx.font = 'bold 80px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('🏠', W / 2, FOTO_H / 2 + 30);
      }
    } else {
      const grad = ctx.createLinearGradient(0, 0, W, FOTO_H);
      grad.addColorStop(0, '#ea580c');
      grad.addColorStop(1, '#c2410c');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, W, FOTO_H);
    }

    // Overlay gradiente na foto (bottom fade)
    const fadeGrad = ctx.createLinearGradient(0, FOTO_H - 200, 0, FOTO_H);
    fadeGrad.addColorStop(0, 'rgba(0,0,0,0)');
    fadeGrad.addColorStop(1, 'rgba(0,0,0,0.55)');
    ctx.fillStyle = fadeGrad;
    ctx.fillRect(0, FOTO_H - 200, W, 200);

    // Badge CÓDIGO no topo esquerdo da foto
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    roundRect(ctx, 28, 28, 180, 52, 26);
    ctx.fill();
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 26px sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText(`Cód. ${data.codigo}`, 50, 62);

    // Badge TIPO no topo direito
    ctx.fillStyle = '#ea580c';
    roundRect(ctx, W - 208, 28, 180, 52, 26);
    ctx.fill();
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 26px sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText(data.tipo.toUpperCase(), W - 50, 62);

    // ── Área de conteúdo (abaixo da foto) ────────────────────────────────────
    const CONTENT_Y = FOTO_H;
    const CONTENT_H = H - FOTO_H;

    // Fundo branco com leve sombra no topo
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, CONTENT_Y, W, CONTENT_H);

    // Linha laranja decorativa no topo do conteúdo
    ctx.fillStyle = '#ea580c';
    ctx.fillRect(0, CONTENT_Y, W, 6);

    // ── PREÇO ─────────────────────────────────────────────────────────────────
    const PRECO_Y = CONTENT_Y + 70;
    ctx.fillStyle = '#ea580c';
    ctx.font = 'bold 72px sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText(formatCurrencyCanvas(data.preco), 56, PRECO_Y);

    // ── TÍTULO ────────────────────────────────────────────────────────────────
    ctx.fillStyle = '#111827';
    ctx.font = 'bold 42px sans-serif';
    const tituloLines = wrapText(ctx, data.titulo, W - 112);
    tituloLines.slice(0, 2).forEach((line, i) => {
      ctx.fillText(line, 56, PRECO_Y + 70 + i * 52);
    });

    // ── LOCALIZAÇÃO ───────────────────────────────────────────────────────────
    const LOC_Y = PRECO_Y + 70 + Math.min(tituloLines.length, 2) * 52 + 30;
    ctx.fillStyle = '#6b7280';
    ctx.font = '34px sans-serif';
    ctx.fillText(`📍  ${data.bairro}, ${data.cidade}`, 56, LOC_Y);

    // ── CARACTERÍSTICAS (chips) ───────────────────────────────────────────────
    const chips: { icon: string; label: string }[] = [];
    if (data.quartos) chips.push({ icon: '🛏', label: `${data.quartos} quartos` });
    if (data.banheiros) chips.push({ icon: '🚿', label: `${data.banheiros} banh.` });
    if (data.vagas) chips.push({ icon: '🚗', label: `${data.vagas} vagas` });
    if (data.area_total) chips.push({ icon: '📐', label: `${data.area_total}m²` });

    const CHIPS_Y = LOC_Y + 56;
    let chipX = 56;
    ctx.font = 'bold 28px sans-serif';
    for (const chip of chips) {
      const text = `${chip.icon}  ${chip.label}`;
      const tw = ctx.measureText(text).width + 48;
      ctx.fillStyle = '#f3f4f6';
      roundRect(ctx, chipX, CHIPS_Y - 36, tw, 52, 26);
      ctx.fill();
      ctx.fillStyle = '#374151';
      ctx.fillText(text, chipX + 24, CHIPS_Y + 4);
      chipX += tw + 16;
      if (chipX > W - 200) break;
    }

    // ── DIVISOR ───────────────────────────────────────────────────────────────
    const DIV_Y = CHIPS_Y + 50;
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(56, DIV_Y);
    ctx.lineTo(W - 56, DIV_Y);
    ctx.stroke();

    // ── RODAPÉ (branding) ─────────────────────────────────────────────────────
    const FOOTER_Y = DIV_Y + 50;

    // Nome do corretor
    ctx.fillStyle = '#111827';
    ctx.font = 'bold 36px sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('Mateus Oliveira', 56, FOOTER_Y);

    ctx.fillStyle = '#6b7280';
    ctx.font = '28px sans-serif';
    ctx.fillText('Corretor de Imóveis · CRECI 12345-F', 56, FOOTER_Y + 42);

    // Telefone com ícone
    ctx.fillStyle = '#ea580c';
    ctx.font = 'bold 30px sans-serif';
    ctx.fillText('📱  (79) 99988-1002', 56, FOOTER_Y + 96);

    // QR / link (texto)
    ctx.fillStyle = '#9ca3af';
    ctx.font = '24px sans-serif';
    ctx.textAlign = 'right';
    const linkShort = `${window.location.origin}/imovel/${data.slug}`;
    ctx.fillText(linkShort, W - 56, FOOTER_Y + 96);

    // Logo / marca d'água laranja no canto
    ctx.fillStyle = '#ea580c';
    ctx.font = 'bold 28px sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText('Mateus Oliveira Imóveis', W - 56, FOOTER_Y);

    return canvas.toDataURL('image/png', 1.0);
  }, []);

  return { generateFlyer };
}
